import * as React from 'react';

import type {ActivityTypeIcon} from 'nutshell-graphql-types';

import {getIconForActivityType} from '../../../../../../ui/setup/activity-types/helpers';

import {colors} from '../../colors/colors';
import {ClockRegularIcon} from '../../icon/svg/clock-regular-icon';
import {LockRegularIcon} from '../../icon/svg/lock-regular-icon';
import {PencilOutlineIcon} from '../../icon/svg/pencil-outline-icon';
import {PeopleRegularIcon} from '../../icon/svg/people-regular-icon';
import {TruncatedGroup} from '../../limited-group/truncated-group';
import type {Participant} from '../../timeline/types';
import {Body} from '../../typography/body';
import {useResizeObserver} from '../../utils/hooks/use-resize-observer';

import {BaseRow} from '../base-row/base-row';

import {ActivityDateAndTime} from './activity-date-and-time';

import styles from './activity-row.module.css';

type Props = {
    title: string;
    onClick: () => void;
    activityTypeIconId: ActivityTypeIcon | number;
    startTime: number;
    endTime: number;
    isAllDay: boolean;
    isOverdue: boolean;
    participants: Participant[];
    isPrivate: boolean;
};

export function ActivityRow(props: Props) {
    const containerRef = React.useRef<HTMLDivElement | null | undefined>(null);
    const [containerWidth, setContainerWidth] = React.useState<number>(0);

    const [width] = useResizeObserver(containerRef.current);

    React.useEffect(() => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.getBoundingClientRect().width);
        }
    }, [containerRef]);

    React.useEffect(() => {
        if (typeof width === 'number') {
            setContainerWidth(width);
        }
    }, [width]);

    const Icon = getIconForActivityType(props.activityTypeIconId);

    return (
        <BaseRow
            type='activities'
            onClick={props.onClick}
            title={props.title}
            customIcon={<Icon size={16} color={colors.greyLt} />}
            toolbarProps={{
                options: [
                    {
                        id: 'edit',
                        icon: PencilOutlineIcon,
                        tooltipText: 'Edit activity',
                        onClick: props.onClick,
                    },
                ],
            }}
            statusBadges={
                props.isPrivate && NutClientConfig.hasActivitySyncContactCreationFF
                    ? [
                          {
                              text: 'Private',
                              icon: LockRegularIcon,
                              level: 'info--grey',
                              detail: 'This activity is private',
                          },
                      ]
                    : undefined
            }
        >
            <div className='flex gap-6'>
                <div className='flex flex-dir-col gap-2'>
                    {/* @ts-expect-error */}
                    <ClockRegularIcon size={16} color={colors.grey} />
                    {/* @ts-expect-error */}
                    <PeopleRegularIcon size={16} color={colors.grey} />
                </div>
                <div className='flex flex-dir-col gap-4 full-width'>
                    <ActivityDateAndTime
                        startTime={props.startTime}
                        endTime={props.endTime}
                        isAllDay={props.isAllDay}
                        isOverdue={props.isOverdue}
                    />
                    {/* @ts-expect-error */}
                    <div ref={containerRef} className={styles['truncated-group']}>
                        {props.participants.length > 0 ? (
                            <TruncatedGroup
                                participants={props.participants}
                                maxWidth={containerWidth}
                                showEmailAddressTooltip={true}
                            />
                        ) : (
                            <Body size='small' color='medium'>
                                No participants
                            </Body>
                        )}
                    </div>
                </div>
            </div>
        </BaseRow>
    );
}
